import React, { useCallback } from 'react';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import { useModalActions } from '../../Modals/modalActions';
import { ContractView } from '../pages/ContractView';
import Button from '@cube3/ui/src/Buttons/Button';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { Typography } from '@cube3/ui/src/typography/Typography';

import { useContractDuplication } from '../hooks/useContractDuplication';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { Shimmer } from '@cube3/cubicle/src/core/atoms/Loader/Shimmer';
import { ContractCommandBar } from '../prefabs/command-bars/ContractCommandBar';
import { useHistory, useLocation } from 'react-router';
import Time from '@cube3/cubicle/src/core/utility/Time';
import { ContractTitle } from '@cube3/cubicle/src/core/molecules/SuggestionItems/ContractSuggestionItem';

interface Props {
  modalContext: {
    contractId: string;
    onCloseEvent?: () => void;
  };
}

export const ManageContractModal: React.FunctionComponent<Props> = (props) => {
  const { contractId, onCloseEvent } = props.modalContext;
  const modalActions = useModalActions();

  const { loading } = useContractDuplication(contractId);

  const history = useHistory();
  const location = useLocation();
  const goBack = useCallback(() => {
    onCloseEvent && onCloseEvent();
    modalActions.previousModal();
    const backlink = location.state?.['from'];
    if (backlink) {
      history.push(backlink, location.state);
    }
  }, [modalActions, location, onCloseEvent]);

  return (
    <ModalMenuUI
      wide={true}
      loading={loading}
      onCloseEvent={goBack}
      title="Manage Contract"
      footerRightComponent={
        <Button colorVariant="filled1" onClick={goBack} text="Done" />
      }
      topBarComponent={<ContractCommandBar contractId={contractId} />}
    >
      <ManageContractHeader contractId={contractId} />
      <ContractView contractId={contractId} />
    </ModalMenuUI>
  );
};

export const ManageContractHeader = (props) => {
  const { contractId } = props;
  const contract = useResource__ALPHA({
    resourceType: 'contract',
    resourceId: contractId
  });

  const contractType = useResource__ALPHA({
    resourceType: 'contract-type',
    resourceId: contract.resource?.relationships.contract_type?.id
  });
  const workspace = useCurrentWorkspace()[1];

  if (!contract.resource || !contractType.resource) {
    return <Shimmer height={30} variant="rectangle" />;
  }

  return (
    <>
      <div>
        <ContractTitle
          contract={contract.resource}
          expiresSoonOffsetDay={workspace?.first_contract_reminder}
        />
      </div>
      <div>
        <Typography typographyStyle="body2" color="contrast2" display="inline">
          {contractType.resource.display_name}
        </Typography>
        {contract.resource?.expires_at && (
          <>
            <Typography
              typographyStyle="body2"
              color="contrast2"
              display="inline"
            >
              {' '}
              •{' '}
            </Typography>
            <Typography
              typographyStyle="body2"
              color="contrast2"
              display="inline"
            >
              Valid until{' '}
              <Time
                date={new Date(contract.resource.expires_at)}
                noTime={true}
                toolTip={false}
                placement="top"
              />
            </Typography>
          </>
        )}
      </div>
    </>
  );
};
